import { TFunction } from "i18next";
import { z } from "zod";

import type { CampaignBudgetMinimums } from "./api/useCampaignBudgetMinimums";
import { MAX_MONTHLY_BUDGET, MIN_MONTHLY_BUDGET } from "./constants";

function getEffectiveMinimum(minimums?: CampaignBudgetMinimums | null): number {
  if (!minimums) return MIN_MONTHLY_BUDGET;

  const candidates: number[] = [MIN_MONTHLY_BUDGET];
  if (minimums.minimumMonthlyBudget != null)
    candidates.push(minimums.minimumMonthlyBudget);
  if (minimums.minimumBudgetPerVacancy != null)
    candidates.push(minimums.minimumBudgetPerVacancy * minimums.vacancyCount);

  return Math.max(...candidates);
}

type SchemaOptions = {
  preventEndDateClear?: boolean;
  isIndeed?: boolean;
};

export const createCampaignJobBoardSchema = (
  t: TFunction,
  isEdit: boolean = false,
  minimums?: CampaignBudgetMinimums | null,
  options?: SchemaOptions,
) => {
  const effectiveMin = isEdit ? MIN_MONTHLY_BUDGET : getEffectiveMinimum(minimums);
  return z
    .object({
      clientIntegrationModuleId: isEdit
        ? z.string()
        : z
          .string()
          .min(
            1,
            t("errors.requiredField", { fieldName: t("common.jobBoard") })
          ),
      currentMonthBudget: z
        .number({
          error: t("errors.number", {
            field: `${t("common.current")} ${t("common.month")} ${t(
              "common.budget"
            )}`,
          }),
        })
        .int(
          t("errors.integer", {
            field: `${t("common.current")} ${t("common.month")} ${t(
              "common.budget"
            )}`,
          })
        )
        .min(1, {
          message: t("errors.budgetMin", {
            fieldName: `${t("common.current")} ${t("common.month")} ${t(
              "common.budget"
            )}`,
            min: "1",
          }),
        })
        .optional(),
      startDate: z
        .string()
        .nonempty(
          t("errors.requiredField", { fieldName: t("common.startDate") })
        ),
      endDate: z.string().optional(),
      monthlyBudget: z
        .number({
          error: t("errors.number", {
            field: `${t("common.monthly")} ${t("common.budget")}`,
          }),
        })
        .int(
          t("errors.integer", {
            field: `${t("common.monthly")} ${t("common.budget")}`,
          })
        )
        .min(
          effectiveMin,
          t("errors.budgetMin", {
            fieldName: `${t("common.monthly")} ${t("common.budget")}`,
            min: effectiveMin.toLocaleString(),
          })
        )
        .max(
          MAX_MONTHLY_BUDGET,
          t("errors.budgetMax", {
            fieldName: `${t("common.monthly")} ${t("common.budget")}`,
            max: "9,999,999",
          })
        ),
      useDifferentCurrentMonthBudget: z.boolean().default(false),
      // Indeed-only, create-only. The prorated first-month estimate is intentionally allowed to
      // fall below the minimum monthly budget (Indeed prorates server-side), so no min check here.
      useProRataFirstMonth: z.boolean().default(false),
    })
    .superRefine((data, ctx) => {
      const now = new Date();
      const currentYear = now.getFullYear();
      const currentMonth = now.getMonth();

      const lastDayOfNextMonth = new Date(currentYear, currentMonth + 2, 0);

      const startDate = new Date(data.startDate);
      startDate.setHours(0, 0, 0, 0);

      const endDate = data.endDate ? new Date(data.endDate) : null;
      if (endDate) endDate.setHours(0, 0, 0, 0);

      if (startDate.getTime() > lastDayOfNextMonth.getTime()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t("errors.startDateInFuture"),
          path: ["startDate"],
        });
      }

      if (endDate && endDate.getTime() <= startDate.getTime()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t("errors.endDateBeforeStart"),
          path: ["endDate"],
        });
      }

      if (options?.preventEndDateClear && !data.endDate) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t("admin.campaigns.jobBoard.indeedEndDateCannotBeCleared", {
            defaultValue:
              "Indeed does not allow removing an end date once set. You can change it to a different date.",
          }),
          path: ["endDate"],
        });
      }

      if (options?.isIndeed && endDate) {
        const centralDateStr = new Date().toLocaleDateString("en-CA", {
          timeZone: "America/Chicago",
        });
        const [y, m, d] = centralDateStr.split("-").map(Number);
        const centralToday = new Date(y, m - 1, d);
        if (endDate.getTime() <= centralToday.getTime()) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t("admin.campaigns.jobBoard.indeedEndDateMustBeFuture", {
              defaultValue:
                "Indeed end date must be after today in US Central Time.",
            }),
            path: ["endDate"],
          });
        }
      }

      if (data.useDifferentCurrentMonthBudget && !data.currentMonthBudget) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t("errors.requiredField", {
            fieldName: `${t("common.current")} ${t("common.month")} ${t(
              "common.budget"
            )}`,
          }),
          path: ["currentMonthBudget"],
        });
      }

      if (
        data.useDifferentCurrentMonthBudget &&
        data.currentMonthBudget &&
        data.currentMonthBudget < effectiveMin
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t("errors.budgetMin", {
            fieldName: `${t("common.current")} ${t("common.month")} ${t("common.budget")}`,
            min: effectiveMin.toLocaleString(),
          }),
          path: ["currentMonthBudget"],
        });
      }

      if (
        data.useDifferentCurrentMonthBudget &&
        data.currentMonthBudget &&
        data.monthlyBudget
      ) {
        const maxAllowed = data.monthlyBudget * 2; // 200% limit
        if (data.currentMonthBudget > maxAllowed) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t("validation.budgetExceedsLimit", {
              defaultValue:
                "Current month budget cannot exceed {{max}}% of the monthly budget ({{maxAmount}})",
              max: "200",
              maxAmount: maxAllowed.toLocaleString(),
            }),
            path: ["currentMonthBudget"],
          });
        }
      }

      const startYear = startDate.getFullYear();
      const startMonth = startDate.getMonth();

      const isStartDateInNextMonthOrLater =
        startYear > currentYear ||
        (startYear === currentYear && startMonth > currentMonth);

      if (isStartDateInNextMonthOrLater) {
        return;
      }
    });
};

export type CampaignJobBoardSchema = ReturnType<
  typeof createCampaignJobBoardSchema
>;
